import {
  INBOX_TYPES,
  TWILIO_CHANNEL_MEDIUM,
  isVoiceCallEnabled,
  getInboxVoiceIcon,
} from 'dashboard/helper/inbox';
import { computed } from 'vue';

const channelTypeIconMap = {
  'Channel::Api': 'i-woot-api',
  'Channel::Email': 'i-woot-mail',
  'Channel::FacebookPage': 'i-woot-messenger',
  'Channel::Line': 'i-woot-line',
  'Channel::Sms': 'i-woot-sms',
  'Channel::Telegram': 'i-woot-telegram',
  'Channel::TwilioSms': 'i-woot-sms',
  'Channel::TwitterProfile': 'i-woot-x',
  'Channel::WebWidget': 'i-lucide-globe',
  'Channel::Whatsapp': 'i-woot-whatsapp',
  'Channel::Instagram': 'i-woot-instagram',
  'Channel::Tiktok': 'i-woot-tiktok',
};

const providerIconMap = {
  microsoft: 'i-woot-outlook',
  google: 'i-woot-gmail',
};

// Full-color brand icons. Most come from the `logos` Iconify set; Instagram,
// Outlook and Line use custom `woot` glyphs since the `logos` versions are
// monochrome or missing. Channels not listed here have no brand variant and
// callers should fall back to the monochrome glyph via useChannelIcon.
const channelTypeBrandIconMap = {
  'Channel::FacebookPage': 'i-logos-messenger',
  'Channel::Line': 'i-woot-line-color',
  'Channel::Telegram': 'i-logos-telegram',
  'Channel::Whatsapp': 'i-logos-whatsapp-icon',
  'Channel::Instagram': 'i-woot-instagram-color',
  'Channel::Tiktok': 'i-logos-tiktok-icon',
};

const providerBrandIconMap = {
  microsoft: 'i-woot-outlook-color',
  google: 'i-logos-google-gmail',
};

const resolveInbox = inbox => inbox?.value ?? inbox;

const CHANNEL_ICON_COLOR_MAP = {
  'Channel::Api': 'text-n-amber-11',
  'Channel::Email': 'text-n-blue-11',
  'Channel::FacebookPage': 'text-n-blue-11',
  'Channel::Line': 'text-n-teal-11',
  'Channel::Sms': 'text-n-amber-11',
  'Channel::Telegram': 'text-n-blue-11',
  'Channel::TwilioSms': 'text-n-amber-11',
  'Channel::TwitterProfile': 'text-n-slate-12',
  'Channel::WebWidget': 'text-n-iris-11',
  'Channel::Whatsapp': 'text-n-teal-11',
  'Channel::Instagram': 'text-n-ruby-11',
  'Channel::Tiktok': 'text-n-slate-12',
};

const CHANNEL_ICON_FRAME_MAP = {
  'Channel::Api': 'border-n-amber-7 bg-n-amber-3/60',
  'Channel::Email': 'border-n-blue-7 bg-n-blue-3/60',
  'Channel::FacebookPage': 'border-n-blue-7 bg-n-blue-3/60',
  'Channel::Line': 'border-n-teal-7 bg-n-teal-3/60',
  'Channel::Sms': 'border-n-amber-7 bg-n-amber-3/60',
  'Channel::Telegram': 'border-n-blue-7 bg-n-blue-3/60',
  'Channel::TwilioSms': 'border-n-amber-7 bg-n-amber-3/60',
  'Channel::TwitterProfile': 'border-n-slate-7 bg-n-slate-3/60',
  'Channel::WebWidget': 'border-n-iris-7 bg-n-iris-3/60',
  'Channel::Whatsapp': 'border-n-teal-7 bg-n-teal-3/60',
  'Channel::Instagram': 'border-n-ruby-7 bg-n-ruby-3/60',
  'Channel::Tiktok': 'border-n-slate-7 bg-n-slate-3/60',
};

const getInboxDetails = inbox => inbox.value || inbox || {};

const resolveChannelType = inbox => {
  const inboxDetails = getInboxDetails(inbox);
  const isEvolutionApiInbox =
    inboxDetails.channel_type === INBOX_TYPES.API &&
    inboxDetails.additional_attributes?.is_evolution_api_integration;

  if (isEvolutionApiInbox) {
    return INBOX_TYPES.WHATSAPP;
  }

  if (
    inboxDetails.channel_type === 'Channel::TwilioSms' &&
    inboxDetails.medium === 'whatsapp'
  ) {
    return 'Channel::Whatsapp';
  }
  return inboxDetails.channel_type;
};

export function useChannelIcon(inbox) {
  const channelIcon = computed(() => {
    const inboxDetails = resolveInbox(inbox);
    const type = resolveChannelType(inboxDetails);
    let icon = channelTypeIconMap[type];

    if (
      inboxDetails.channel_type === INBOX_TYPES.EMAIL &&
      inboxDetails.provider
    ) {
      if (Object.keys(providerIconMap).includes(inboxDetails.provider)) {
        icon = providerIconMap[inboxDetails.provider];
      }
    }

    // Special case for Twilio whatsapp
    if (
      type === INBOX_TYPES.TWILIO &&
      inboxDetails.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP
    ) {
      icon = 'i-woot-whatsapp';
    }

    // Voice-enabled inboxes use the combined channel + voice-wave badge glyph.
    if (isVoiceCallEnabled(inboxDetails)) {
      icon = getInboxVoiceIcon(type, inboxDetails.medium);
    }

    return icon ?? 'i-ri-global-fill';
  });

  return channelIcon;
}

export function useChannelBrandIcon(inbox) {
  return computed(() => {
    const inboxDetails = resolveInbox(inbox);
    const type = resolveChannelType(inboxDetails);
    let icon = channelTypeBrandIconMap[type];

    if (
      inboxDetails.channel_type === INBOX_TYPES.EMAIL &&
      inboxDetails.provider
    ) {
      if (Object.keys(providerBrandIconMap).includes(inboxDetails.provider)) {
        icon = providerBrandIconMap[inboxDetails.provider];
      }
    }

    if (
      type === INBOX_TYPES.TWILIO &&
      inboxDetails.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP
    ) {
      icon = channelTypeBrandIconMap['Channel::Whatsapp'];
    }

    return icon ?? null;
  });
}

export function useChannelIconColor(inbox) {
  return computed(() => {
    const inboxDetails = getInboxDetails(inbox);

    if (isVoiceCallEnabled(inboxDetails)) {
      return 'text-n-teal-11';
    }

    return (
      CHANNEL_ICON_COLOR_MAP[resolveChannelType(inbox)] ?? 'text-n-slate-10'
    );
  });
}

export function useChannelIconFrame(inbox) {
  return computed(() => {
    const inboxDetails = getInboxDetails(inbox);

    if (isVoiceCallEnabled(inboxDetails)) {
      return 'border-n-teal-7 bg-n-teal-3/60';
    }

    return (
      CHANNEL_ICON_FRAME_MAP[resolveChannelType(inbox)] ??
      'border-n-slate-7 bg-n-slate-3/60'
    );
  });
}
